/**
 * Organization Call to Action (CTA) Types
 *
 * Defines types for organization-level CTAs with simplified reward model.
 * Organizations use offer selection instead of inline voucher/gift configuration.
 */

import type { UUID } from "./common.types";

/**
 * CTA Type - same as events
 * - subscription: U.S. LawShield signup with auto-populated parameters
 * - other: Custom URL with manual parameters
 */
export type OrganizationCTAType = "subscription" | "other";

/**
 * Reward Type for Organizations
 * - voucher: Generate discount voucher (inline configuration)
 * - offer: Link to an existing offer from the offers table
 */
export type OrganizationCTARewardType = "voucher" | "offer";

/**
 * Offer summary for display in CTA
 */
export interface CTAOfferSummary {
  id: UUID;
  name: string;
  offer_code: string;
  offer_type?: string;
  value_type?: string;
  value_amount?: number;
  description?: string;
}

/**
 * Location summary for CTA display
 */
export interface CTALocationSummary {
  id: UUID;
  name: string;
  state?: string;
  city?: string;
  affiliate_id?: string;
}

/**
 * Organization CTA entity
 */
export interface OrganizationCTA {
  id: UUID;
  organization_id: UUID;
  /** Optional location ID - when set, URL uses this location's state */
  location_id?: UUID;
  /** Joined location data when fetched with location details */
  location?: CTALocationSummary;
  name: string;
  cta_type: OrganizationCTAType;
  base_url?: string;
  parameters: Record<string, string>;
  generated_url?: string;
  qr_code_url?: string;
  reward_type: OrganizationCTARewardType;
  offer_id?: UUID;
  /** Joined offer data when fetched with offer details */
  offer?: CTAOfferSummary;
  /** Voucher configuration (when reward_type = 'voucher') */
  voucher_amount?: number;
  voucher_start_date?: string;
  voucher_end_date?: string;
  /** Number of days from voucher creation until expiration. Default 30. */
  voucher_expiry_days: number;
  include_state_param: boolean;
  use_state_defaults: boolean;
  /** When true the generated URL carries singlevoucher=1. Defaults OFF so existing QR codes are unchanged. */
  single_voucher: boolean;
  /** When true the generated URL omits discountvalue and the landing page must call the
   *  cta-voucher-value edge function. False by default: existing QR codes carry the amount. */
  allow_variable_voucher_value: boolean;
  use_member_signup?: boolean;
  display_away_plans?: boolean;
  is_active: boolean;
  /** Smart Admin replacement R2B (2026-05-17). Optional rep attribution
   *  — same semantics as event CTAs. Vouchers generated via this CTA
   *  attribute their "credited rep" to this position for downstream
   *  commission/reporting. NULL = generic fallback CTA (no specific rep).
   */
  sales_position_id?: UUID | null;
  /** MSR EVT-04 (mig 530): the event activity (events.type) this CTA is for; null = any activity. */
  activity_type?: string | null;
  created_at: string;
  updated_at: string;
  created_by?: UUID;
  updated_by?: UUID;
}

/**
 * Input for creating a new organization CTA
 */
export interface CreateOrganizationCTAInput {
  organization_id: UUID;
  /** Optional location ID - when set, URL uses this location's state */
  location_id?: UUID;
  name: string;
  cta_type: OrganizationCTAType;
  base_url?: string;
  parameters?: Record<string, string>;
  reward_type?: OrganizationCTARewardType;
  offer_id?: UUID;
  voucher_amount?: number;
  voucher_start_date?: string;
  voucher_end_date?: string;
  voucher_expiry_days?: number;
  include_state_param?: boolean;
  use_state_defaults?: boolean;
  single_voucher?: boolean;
  allow_variable_voucher_value?: boolean;
  use_member_signup?: boolean;
  display_away_plans?: boolean;
  /** R2B (2026-05-17): optional rep attribution. */
  sales_position_id?: UUID | null;
  /** MSR EVT-04 (mig 530): the event activity this CTA is for; null/omitted = any activity. */
  activity_type?: string | null;
}

/**
 * Input for updating an organization CTA
 */
export interface UpdateOrganizationCTAInput {
  name?: string;
  /** Optional location ID - when set, URL uses this location's state */
  location_id?: UUID | null;
  cta_type?: OrganizationCTAType;
  base_url?: string;
  parameters?: Record<string, string>;
  reward_type?: OrganizationCTARewardType;
  offer_id?: UUID | null;
  voucher_amount?: number | null;
  voucher_start_date?: string | null;
  voucher_end_date?: string | null;
  voucher_expiry_days?: number;
  generated_url?: string;
  qr_code_url?: string;
  is_active?: boolean;
  include_state_param?: boolean;
  use_state_defaults?: boolean;
  single_voucher?: boolean;
  allow_variable_voucher_value?: boolean;
  use_member_signup?: boolean;
  display_away_plans?: boolean;
  /** R2B (2026-05-17): operator can change or clear rep attribution. */
  sales_position_id?: UUID | null;
  /** MSR EVT-04 (mig 530): the event activity this CTA is for; null = any activity. */
  activity_type?: string | null;
}

/**
 * Offer available for organization CTA selection
 * Returned by get_offers_for_organization function
 */
export interface AvailableOffer {
  id: UUID;
  offer_code: string;
  name: string;
  description?: string;
  offer_type?: string;
  value_type?: string;
  value_amount?: number;
  is_active: boolean;
}

/**
 * Subscription CTA parameter keys (same as events)
 */
export const SUBSCRIPTION_CTA_PARAM_KEYS = {
  affid: "affid",
  promo: "promo",
  altprice: "altprice",
  utm_source: "utm_source",
  utm_term: "utm_term",
  utm_campaign: "utm_campaign",
  utm_medium: "utm_medium",
  utm_content: "utm_content",
  // Auto-populated (not user-editable):
  // ste: organization.address_state (2-char state code)
  // ctaid: CTA ID for tracking
  // discountvalue: from linked offer's value_amount
  // retail: fixed value "1" for organization voucher CTAs
} as const;

/**
 * Fields that are editable by users for subscription CTAs
 */
export const SUBSCRIPTION_EDITABLE_FIELDS = [
  "affid",
  "promo",
  "altprice",
  "utm_source",
  "utm_term",
  "utm_campaign",
  "utm_medium",
  "utm_content",
] as const;

/**
 * Base URL for subscription CTAs (from environment)
 */
export const SUBSCRIPTION_CTA_BASE_URL =
  import.meta.env.VITE_SUBSCRIPTION_CTA_BASE_URL ||
  "https://ww2.uslawshield.com/voucher/";
